import type { SayThisNotThatRuleInput } from 'app/generated/graphql'

/**
 * "Say this, not that": wording a practice heard from its agent and wants said differently. Each
 * rule pairs the phrase to stop using with the one to use instead, on calls, texts, or both.
 */
export type PhraseRule = SayThisNotThatRuleInput

export const PHRASE_RULE_CHANNELS = [
  { value: `both`, label: `Calls and texts` },
  { value: `voice`, label: `Calls` },
  { value: `sms`, label: `Texts` },
]

/** The API's limits, shown so a practice sees them before Save refuses. */
export const MAX_PHRASE_RULES = 20
export const MAX_PHRASE_LENGTH = 100

/** The rules as they would be saved: trimmed, with empty rows dropped. */
export function cleanPhraseRules(rules: PhraseRule[]): PhraseRule[] {
  return rules
    .map((rule) => ({
      stopPhrase: rule.stopPhrase.trim(),
      replacementPhrase: rule.replacementPhrase.trim(),
      channel: rule.channel,
    }))
    .filter((rule) => rule.stopPhrase || rule.replacementPhrase)
}

/** Whether two lists would save differently; blank rows and stray spaces do not count. */
export function phraseRulesDiffer(a: PhraseRule[], b: PhraseRule[]): boolean {
  return JSON.stringify(cleanPhraseRules(a)) !== JSON.stringify(cleanPhraseRules(b))
}

/** Why the rules cannot be saved yet, or undefined when they can. */
export function phraseRulesProblem(rules: PhraseRule[]): string | undefined {
  const cleaned = cleanPhraseRules(rules)
  if (cleaned.some((rule) => !rule.stopPhrase || !rule.replacementPhrase))
    return `Each Say This, Not That phrase needs both sides filled in.`
  if (cleaned.length > MAX_PHRASE_RULES) return `Say This, Not That holds up to ${MAX_PHRASE_RULES} phrases.`
  if (
    cleaned.some(
      (rule) => rule.stopPhrase.length > MAX_PHRASE_LENGTH || rule.replacementPhrase.length > MAX_PHRASE_LENGTH,
    )
  )
    return `Each Say This, Not That phrase is up to ${MAX_PHRASE_LENGTH} characters.`
  return undefined
}
